import { signedFetch as rawSignedFetch } from '../../super-picker/services/api';
import {
  demoClientsResult, demoPipeline, demoPipelineCounts, demoDashboardSummary,
  demoAging, demoWATypeDistribution, demoClientDetail, DEMO_TAGS, DEMO_HYPING_PROJECTS,
  demoTemplates, demoCampaigns, demoAgentSettings, demoWhatsAppStatus,
  demoWhatsAppCloudStatus,
  demoCalendlyEventTypes, demoLatestPickerSalesUpdate, demoEngagementToday,
  demoClientEngagement,
} from '../demo/demoData';

const PREFIX = '/api/super-connector';

// ─── Demo mode ───────────────────────────────────────────────────────────
// When on (non-Agent users, or an admin previewing the Demo view), the app must
// not touch the gated backend — every protected GET would 403 and could trigger
// a redirect. So reads are served from local fixtures (see demo/demoData.ts and
// the guards in each read fn) and any mutation is blocked with a friendly prompt.
let __demoMode = false;
/** True only for logged-in users with no Super Connector access. Granted users previewing Demo do not get the register-interest prompt. */
let __demoInterest = false;
export function setDemoMode(on: boolean): void { __demoMode = on; }
export function isDemoMode(): boolean { return __demoMode; }
export function setDemoInterest(on: boolean): void { __demoInterest = on; }
export function isDemoInterest(): boolean { return __demoInterest; }

/** Thrown by mutations while in demo mode; message is user-facing. */
export class DemoBlockedError extends Error {
  constructor() {
    super(
      __demoInterest
        ? 'This is a demo. Register your interest to unlock full access.'
        : 'This is a demo.',
    );
    this.name = 'DemoBlockedError';
  }
}

/** Notify the app shell to surface the "register interest" demo prompt. */
function notifyDemoBlocked(): void {
  try { window.dispatchEvent(new CustomEvent('sc-demo-blocked')); } catch { /* ignore */ }
}

/**
 * Block a click that would open a real connect flow. Same prompt as a demo-mode
 * write: the banner snackbar, plus DemoBlockedError for an inline message.
 */
export function blockDemoMutation(): never {
  notifyDemoBlocked();
  throw new DemoBlockedError();
}

/**
 * Wrapper around the shared signedFetch. In demo mode it blocks all writes and
 * returns a benign empty response for any un-fixtured GET, so no gated endpoint
 * is ever hit. All calls in this file already go through `signedFetch`, so they
 * inherit this behaviour automatically.
 */
// Endpoints a demo (no-access) user is legitimately allowed to hit — the
// JWT-only payment routes (paying is what grants access) exempt from the gate.
const DEMO_ALLOWED_PATHS = ['/payment/config', '/payment/initiate', '/payment/status/', '/payment-requests/mine'];

async function signedFetch(
  input: string,
  init?: RequestInit,
  // Forwarded to the shared signer: pass false for fire-and-forget calls that
  // must never bounce the window to /unauthorized (or /sso) on a 401.
  redirectOnAuthError: boolean = true,
): Promise<Response> {
  if (__demoMode) {
    const allowed = DEMO_ALLOWED_PATHS.some((p) => input.includes(p));
    if (!allowed) {
      const method = (init?.method || 'GET').toUpperCase();
      if (method !== 'GET') {
        notifyDemoBlocked();
        throw new DemoBlockedError();
      }
      // Un-fixtured GET in demo — return empty JSON so callers don't error/redirect.
      return new Response('[]', { status: 200, headers: { 'Content-Type': 'application/json' } });
    }
  }
  return rawSignedFetch(input, init, true, redirectOnAuthError);
}

// ─── Types ───
export interface ConnectorUser {
  id: string;
  name: string;
  identifier: string;
  email?: string;
  role?: string;
  photo?: string;
  mobile?: string;
  /** Present when this session is an admin logged in as this user. */
  impersonatedBy?: { cea: string; name: string } | null;
}

export interface Client {
  Id: string;
  CEANumber: string;
  Name: string;
  Mobile?: string | null;
  /** Display-only company / organisation name (not collected in Quick Create yet). */
  CompanyName?: string | null;
  Type?: string | null;
  Budget?: number | null;
  PropertyType?: string | null;
  PreferredLocation?: string | null;
  Note?: string | null;
  DOB?: string | null;
  Address?: string | null;
  Stage: string;
  Email?: string | null;
  /** Primary project the agent was hyping (first of HypingProjects). */
  HypingProjectId?: string | null;
  HypingProject?: string | null;
  /**
   * Full set of up to 3 hyped projects (from the child table), attached by read
   * endpoints as an array. May also appear as a legacy JSON string from older
   * cached payloads — parseHypingProjects() handles both.
   */
  HypingProjects?: Array<{ id: string; name: string }> | string | null;
  /** Free-form current location (e.g. from GPS / OneMap). */
  CurrentLocation?: string | null;
  CurrentLocationLat?: number | null;
  CurrentLocationLng?: number | null;
  PreferredLocationLat?: number | null;
  PreferredLocationLng?: number | null;
  /** Customizable tags applied to this client (many-to-many), attached by read endpoints. */
  Tags?: Array<{ Id: string; Name: string; Color?: string | null }> | null;
  /** JSON-encoded array of district codes, e.g. '["D16","D15"]'. */
  InterestedDistricts?: string | null;
  /** Search radius around preferred location in km. */
  DistanceFromPreferredLocation?: number | null;
  EngagementActive?: boolean;
  EngagementLastSentDayNumber?: number | null;
  EngagementLastSentAt?: string | null;
  /**
   * Set when WhatsApp rejected this Mobile as not registered (the hourly cron
   * learns it from the bridge). Null/absent = the number has never been
   * rejected. Cleared on the next successful send and whenever Mobile is edited.
   */
  WhatsAppUnreachableAt?: string | null;
  CreatedAt: string;
  UpdatedAt: string;
  DefunctAt?: string | null;
}

export interface ClientTag {
  Id: string;
  Name: string;
  Color?: string | null;
  /** Number of (non-defunct) clients carrying this tag — populated by fetchTags. */
  ClientCount?: number;
}

/** Preset chip colours offered when creating/editing a tag. */
export const TAG_COLORS: string[] = [
  '#ed8936', // orange (app accent)
  '#3b82f6', // blue
  '#22c55e', // green
  '#a855f7', // purple
  '#ef4444', // red
  '#eab308', // yellow
  '#14b8a6', // teal
  '#ec4899', // pink
  '#64748b', // slate
];

export interface ClientActivity {
  Id: string;
  ClientId: string;
  Type: string;
  Description?: string | null;
  Date: string;
  CEANumber: string;
  CreatedAt: string;
}

export interface ClientPreference {
  Id: string;
  ClientId: string;
  PreferenceType: string;
  BudgetMin?: number | null;
  BudgetMax?: number | null;
  Area?: string | null;
  PropertyType?: string | null;
  SellingAddress?: string | null;
  SellingPrice?: number | null;
  CreatedAt: string;
  UpdatedAt: string;
}

export interface MessageTemplate {
  Id: string;
  CEANumber: string;
  Title: string;
  Body: string;
  Type: string;
  Channel: string;
  /** Official API: Meta approval status of the submitted template. */
  Status?: TemplateApprovalStatus | null;
  TemplateName?: string | null;
  TemplateId?: string | null;
  TemplateSyncedAt?: string | null;
  /** True when the Meta template was submitted from the current body. */
  MetaSynced?: boolean;
  CreatedAt: string;
  UpdatedAt: string;
  DefunctAt?: string | null;
}

export interface Campaign {
  Id: string;
  CEANumber: string;
  Name: string;
  DayNumber: number;
  Channel: string;
  MessageTemplate: string;
  MediaUrl?: string | null;
  CtaLabel?: string | null;
  CtaUrl?: string | null;
  ImageUrl?: string | null;
  VideoUrl?: string | null;
  DocumentUrl?: string | null;
  VoiceUrl?: string | null;
  LinkUrl?: string | null;
  UseCalendly?: boolean;
  /** Custom campaigns: send media as trackable body links instead of attachments. */
  IsMediaTrackable?: boolean;
  /** Custom campaigns: resolved library attachments (from CampaignMedia join). */
  media?: Array<{
    id: string;
    mediaType: string;
    url: string;
    name: string;
    sourceEdmId?: string | null;
    sourceUnavailable?: boolean;
  }>;
  mediaIds?: string[];
  /** One-time campaigns: the exact date ('YYYY-MM-DD') this day is sent. */
  RunDate?: string | null;
  /** Daily sequences: SGT days to wait after this day before the next (1–30). */
  BreakDaysAfter?: number;
  Status?: string | null;
  TemplateName?: string | null;
  TemplateId?: string | null;
  TemplateSyncedAt?: string | null;
  SupersededTemplateId?: string | null;
  SupersededTemplateName?: string | null;
  TemplateContentHash?: string | null;
  IsActive: boolean;
  CreatedAt: string;
  UpdatedAt: string;
}

export interface CampaignDayInput {
  dayNumber: number;
  channel: string;
  /**
   * Whether this day sends. Omit to mean active — days used to signal "off" by
   * being absent from the payload, which threw away their content and made the
   * off state impossible to round-trip. Send it explicitly to switch a day off
   * while keeping what is configured on it.
   */
  isActive?: boolean;
  messageTemplate?: string;
  mediaUrl?: string | null;
  ctaLabel?: string | null;
  ctaUrl?: string | null;
  imageUrl?: string | null;
  videoUrl?: string | null;
  documentUrl?: string | null;
  voiceUrl?: string | null;
  linkUrl?: string | null;
  useCalendly?: boolean;
  isMediaTrackable?: boolean;
  /** Custom campaigns: library media ids attached to this day. */
  mediaIds?: string[];
  runDate?: string | null;
  /** Daily sequences: SGT days to wait after this day before the next (1–30). */
  breakDaysAfter?: number;
  status?: string | null;
  templateName?: string | null;
  templateId?: string | null;
  templateSyncedAt?: string | null;
  supersededTemplateId?: string | null;
  supersededTemplateName?: string | null;
  templateContentHash?: string | null;
}

export interface AgentSettings {
  hasCalendlyKey: boolean;
  calendlyEventTypeUri: string | null;
  calendlyUserUri: string | null;
  whatsAppStatus: 'disconnected' | 'pairing' | 'connected' | 'error' | string;
  whatsAppPhoneNumber: string | null;
  whatsAppPairedAt: string | null;
  /** Active send transport: paired Agent WhatsApp, or the Official WhatsApp API. */
  whatsAppProvider: WhatsAppProvider;
  engagementStartDayOffset: number;
  engagementActive: boolean;
  engagementMessagesPerHour: number;
  /** Soft daily Official API cap in SGD; null = unlimited. */
  whatsAppCloudDailyBudgetSgd?: number | null;
}

export interface CalendlyEventType {
  uri: string;
  name: string;
  slug: string;
  scheduling_url: string;
  duration: number;
  active: boolean;
  color?: string;
}

export interface CalendlyUser {
  uri: string;
  name: string;
  email: string;
  scheduling_url: string;
  timezone: string;
}

export interface CampaignExecution {
  Id: string;
  ClientId: string;
  CampaignId: string;
  DayNumber: number;
  SentAt?: string | null;
  Status: string;
  LastError?: string | null;
  CreatedAt: string;
  UpdatedAt: string;
}

export interface DashboardSummary {
  totalClients: number;
  totalWA: number;
  stageCounts: Array<{ stage: string; count: string }>;
  typeCounts: Array<{ type: string; count: string }>;
  clientTrend: Array<{ date: string; count: string }>;
  waTrend: Array<{ date: string; count: string }>;
  /** Clients captured per hyped project. Optional for backward compatibility. */
  hypingProjectCounts?: Array<{ project: string; count: string }>;
}

export interface AgingData {
  [bucket: string]: Array<{ Id: string; Name: string; Stage: string; daysSinceActivity: number }>;
}

export type Stage = 'new' | 'contacted' | 'viewing' | 'negotiation' | 'won' | 'lost';

export const STAGES: Stage[] = ['new', 'contacted', 'viewing', 'negotiation', 'won', 'lost'];

export const STAGE_LABELS: Record<Stage, string> = {
  new: 'New',
  contacted: 'Responded',
  viewing: 'Appointment',
  negotiation: 'Negotiation',
  won: 'Won',
  lost: 'Lost',
};

export const STAGE_COLORS: Record<Stage, string> = {
  new: '#3b82f6',
  contacted: '#8b5cf6',
  viewing: '#f59e0b',
  negotiation: '#ef4444',
  won: '#22c55e',
  lost: '#6b7280',
};

/**
 * Shown wherever a client carries `WhatsAppUnreachableAt`. Says what was found
 * and what the agent can do about it — the flag clears itself once the number
 * is corrected or a message finally gets through.
 */
export const WHATSAPP_UNREACHABLE_HINT =
  'WhatsApp rejected this number as not registered, so campaign messages cannot be delivered. '
  + 'Correct the mobile number to clear this.';

export const ACTIVITY_TYPES = ['note', 'call', 'meeting', 'viewing', 'whatsapp', 'email', 'stage-change'] as const;

// ─── API Functions ───

/**
 * Apply the admin "admin mode" toggle to a query: `true` → only company-name
 * clients, `false` → only clients without one, `undefined` → no filter (the
 * full set). Ignored server-side for a non-admin, who always sees every client
 * of theirs, company name or not.
 */
function setCompanyScope(sp: URLSearchParams, adminMode?: boolean): void {
  if (adminMode === true) sp.set('companyScope', 'with');
  else if (adminMode === false) sp.set('companyScope', 'without');
}

/** {@link setCompanyScope} as a standalone query string ('' when unscoped). */
function companyScopeQuery(adminMode?: boolean): string {
  const sp = new URLSearchParams();
  setCompanyScope(sp, adminMode);
  const qs = sp.toString();
  return qs ? `?${qs}` : '';
}

export async function fetchUser(): Promise<ConnectorUser> {
  const res = await signedFetch(`${PREFIX}/user/me`);
  if (!res.ok) throw new Error('Unauthorized');
  return res.json();
}

// ─── Per-agent UI preferences (Firestore-backed, persist across devices) ───
export interface ConnectorUiPreferences {
  /** Hide the last 4 digits of client mobile numbers. Defaults to true. */
  maskMobile: boolean;
}

export async function fetchUiPreferences(): Promise<ConnectorUiPreferences> {
  // Demo data carries fake numbers; keep them masked like everything else.
  if (__demoMode) return { maskMobile: true };
  const res = await signedFetch(`${PREFIX}/user/ui-preferences`);
  if (!res.ok) throw new Error('Failed to fetch preferences');
  return res.json();
}

export async function updateUiPreferences(
  patch: Partial<ConnectorUiPreferences>,
): Promise<ConnectorUiPreferences> {
  if (__demoMode) return { maskMobile: patch.maskMobile ?? true };
  const res = await signedFetch(`${PREFIX}/user/ui-preferences`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error('Failed to save preferences');
  return res.json();
}

export async function fetchClients(params?: {
  search?: string;
  type?: string;
  stage?: string;
  /** Campaign enrollment filter: '__7day__' or a custom campaign name. */
  campaign?: string;
  /** 7-day engagement day (1-7): clients whose last sent day is exactly this. */
  engagementDay?: number;
  /** Tag ids to filter by (match ANY). */
  tags?: string[];
  limit?: number;
  offset?: number;
  /**
   * Admin-only "admin mode" toggle. `true` loads clients that HAVE a company
   * name; `false` loads only clients with no company name; omit to load all
   * (used by utility callers). Ignored server-side for a non-admin, who always
   * sees their full client set — company-name clients included.
   */
  adminMode?: boolean;
}): Promise<{ clients: Client[]; total: number }> {
  if (__demoMode) return demoClientsResult({ stage: params?.stage, tags: params?.tags, search: params?.search });
  const sp = new URLSearchParams();
  if (params?.search) sp.set('search', params.search);
  if (params?.type) sp.set('type', params.type);
  if (params?.stage) sp.set('stage', params.stage);
  if (params?.campaign) sp.set('campaign', params.campaign);
  if (params?.engagementDay) sp.set('engagementDay', String(params.engagementDay));
  if (params?.tags && params.tags.length > 0) sp.set('tags', params.tags.join(','));
  if (params?.limit) sp.set('limit', String(params.limit));
  if (params?.offset) sp.set('offset', String(params.offset));
  setCompanyScope(sp, params?.adminMode);
  const res = await signedFetch(`${PREFIX}/clients?${sp.toString()}`);
  if (!res.ok) throw new Error('Failed to fetch clients');
  return res.json();
}

const FETCH_ALL_CLIENTS_PAGE_SIZE = 500;

/** Every client matching `params`, loaded in pages of 500. */
export async function fetchAllClients(
  params?: Omit<NonNullable<Parameters<typeof fetchClients>[0]>, 'limit' | 'offset'>,
): Promise<Client[]> {
  const all: Client[] = [];
  for (let offset = 0; ; offset += FETCH_ALL_CLIENTS_PAGE_SIZE) {
    const { clients, total } = await fetchClients({ ...params, limit: FETCH_ALL_CLIENTS_PAGE_SIZE, offset });
    all.push(...(clients || []));
    if (!clients || clients.length < FETCH_ALL_CLIENTS_PAGE_SIZE || all.length >= total) return all;
  }
}

export async function createClient(data: {
  Name: string;
  Mobile?: string;
  Type?: string;
  Budget?: number;
  PropertyType?: string;
  PreferredLocation?: string;
  CurrentLocation?: string;
  CurrentLocationLat?: number | null;
  CurrentLocationLng?: number | null;
  PreferredLocationLat?: number | null;
  PreferredLocationLng?: number | null;
  InterestedDistricts?: string[];
  InterestedHdbTowns?: string[];
  DistanceFromPreferredLocation?: number | null;
  Note?: string;
  Email?: string;
  HypingProjectId?: string;
  HypingProject?: string;
  HypingProjects?: Array<{ id: string; name: string }>;
  TagIds?: string[];
  /** When omitted, new clients start with 7-Day Engagement OFF. */
  EngagementActive?: boolean;
}): Promise<Client> {
  const res = await signedFetch(`${PREFIX}/clients`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Failed to create client');
  return res.json();
}

export async function fetchClientDetail(id: string): Promise<{
  client: Client;
  preferences: ClientPreference[];
  activities: ClientActivity[];
  whatsappLogs: Array<{ Id: string; MessageBody: string; SentAt: string }>;
}> {
  if (__demoMode) return demoClientDetail(id);
  const res = await signedFetch(`${PREFIX}/clients/${id}`);
  if (!res.ok) throw new Error('Failed to fetch client');
  return res.json();
}

export async function updateClient(id: string, data: Partial<Client>): Promise<Client> {
  const res = await signedFetch(`${PREFIX}/clients/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Failed to update client');
  return res.json();
}

export async function deleteClient(id: string): Promise<void> {
  const res = await signedFetch(`${PREFIX}/clients/${id}`, { method: 'DELETE' });
  if (!res.ok) throw new Error('Failed to delete client');
}

export interface ImportContactInput {
  name?: string;
  mobile?: string;
  type?: string;
  email?: string;
}
export interface ImportContactsResult {
  ok: boolean;
  created: number;
  skipped: number;
  total: number;
}
export async function importClients(
  contacts: ImportContactInput[],
  engagementActive = false,
): Promise<ImportContactsResult> {
  const res = await signedFetch(`${PREFIX}/clients/import`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ contacts, engagementActive }),
  });
  const payload = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(payload.error || 'Failed to import contacts');
  return payload;
}

export async function updateClientStage(id: string, stage: string): Promise<Client> {
  const res = await signedFetch(`${PREFIX}/clients/${id}/stage`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ stage }),
  });
  if (!res.ok) throw new Error('Failed to update stage');
  return res.json();
}

export async function addActivity(clientId: string, data: {
  Type: string;
  Description?: string;
  Date?: string;
}): Promise<ClientActivity> {
  const res = await signedFetch(`${PREFIX}/clients/${clientId}/activities`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Failed to add activity');
  return res.json();
}

export async function savePreference(clientId: string, data: {
  PreferenceType: string;
  BudgetMin?: number;
  BudgetMax?: number;
  Area?: string;
  PropertyType?: string;
  SellingAddress?: string;
  SellingPrice?: number;
}): Promise<ClientPreference> {
  const res = await signedFetch(`${PREFIX}/clients/${clientId}/preferences`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Failed to save preference');
  return res.json();
}

export async function fetchPipeline(adminMode?: boolean): Promise<{
  pipeline: Record<string, Client[]>;
  stages: string[];
}> {
  if (__demoMode) return demoPipeline();
  const sp = new URLSearchParams();
  setCompanyScope(sp, adminMode);
  const res = await signedFetch(`${PREFIX}/pipeline?${sp.toString()}`);
  if (!res.ok) throw new Error('Failed to fetch pipeline');
  return res.json();
}

/**
 * Per-stage client counts for the pipeline board. Cheap GROUP BY — used to
 * render column headers while each column lazy-loads its cards via fetchClients.
 * Scales to 20k+ clients without loading every row.
 */
export async function fetchPipelineCounts(search?: string, adminMode?: boolean): Promise<{
  counts: Record<string, number>;
}> {
  if (__demoMode) return demoPipelineCounts();
  const sp = new URLSearchParams();
  if (search) sp.set('search', search);
  setCompanyScope(sp, adminMode);
  const res = await signedFetch(`${PREFIX}/pipeline/counts?${sp.toString()}`);
  if (!res.ok) throw new Error('Failed to fetch pipeline counts');
  return res.json();
}

export async function generateWhatsAppLink(data: {
  clientId?: string;
  message?: string;
  mobile?: string;
}): Promise<{ deepLink: string; mobile: string }> {
  const res = await signedFetch(`${PREFIX}/whatsapp/deep-link`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Failed to generate link');
  return res.json();
}

/**
 * Send a WhatsApp to a client. On the Official API `templateId` is required
 * and the approved template's stored body is sent instead of `message`.
 */
export async function sendWhatsAppMessage(data: {
  clientId?: string;
  message: string;
  mobile?: string;
  templateId?: string;
}): Promise<{ ok: true; messageId?: string; mobile: string }> {
  const res = await signedFetch(`${PREFIX}/whatsapp/send`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  const payload = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(payload?.error || 'Failed to send WhatsApp');
  }
  return payload;
}

export async function logWhatsApp(data: {
  clientId: string;
  templateId?: string;
  messageBody: string;
}): Promise<void> {
  await signedFetch(`${PREFIX}/whatsapp/log`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export async function findMatches(clientId: string): Promise<{
  matches: Client[];
  sourceClient: Client;
}> {
  const res = await signedFetch(`${PREFIX}/clients/match/find?clientId=${clientId}`);
  if (!res.ok) throw new Error('Failed to find matches');
  return res.json();
}

export async function fetchTemplates(channel?: string): Promise<MessageTemplate[]> {
  if (__demoMode) return demoTemplates().filter((t) => !channel || t.Channel === channel);
  const sp = new URLSearchParams();
  if (channel) sp.set('channel', channel);
  const res = await signedFetch(`${PREFIX}/templates?${sp.toString()}`);
  if (!res.ok) throw new Error('Failed to fetch templates');
  return res.json();
}

export async function createTemplate(data: {
  Title: string;
  Body: string;
  Type: string;
  Channel: string;
}): Promise<MessageTemplate> {
  const res = await signedFetch(`${PREFIX}/templates`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Failed to create template');
  return res.json();
}

export async function updateTemplate(id: string, data: Partial<MessageTemplate>): Promise<MessageTemplate> {
  const res = await signedFetch(`${PREFIX}/templates/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Failed to update template');
  return res.json();
}

/** Official API: pull live Meta approval state for every message template. */
export async function refreshTemplateStatuses(): Promise<MessageTemplate[]> {
  if (__demoMode) return demoTemplates();
  const res = await signedFetch(`${PREFIX}/templates/refresh-meta`, { method: 'POST' });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Failed to refresh template status');
  return res.json();
}

export async function deleteTemplate(id: string): Promise<void> {
  await signedFetch(`${PREFIX}/templates/${id}`, { method: 'DELETE' });
}

// ─── Client Tags ───
export async function fetchTags(options?: { redirectOnAuthError?: boolean }): Promise<ClientTag[]> {
  if (__demoMode) return DEMO_TAGS;
  const res = await signedFetch(`${PREFIX}/tags`, undefined, options?.redirectOnAuthError ?? true);
  if (!res.ok) throw new Error('Failed to fetch tags');
  return res.json();
}

export async function createTag(
  data: { Name: string; Color?: string | null },
  options?: { redirectOnAuthError?: boolean },
): Promise<ClientTag> {
  const res = await signedFetch(`${PREFIX}/tags`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  }, options?.redirectOnAuthError ?? true);
  const payload = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(payload.error || 'Failed to create tag');
  return payload;
}

export async function updateTag(id: string, data: { Name?: string; Color?: string | null }): Promise<ClientTag> {
  const res = await signedFetch(`${PREFIX}/tags/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  const payload = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(payload.error || 'Failed to update tag');
  return payload;
}

export async function deleteTag(id: string): Promise<void> {
  const res = await signedFetch(`${PREFIX}/tags/${id}`, { method: 'DELETE' });
  const payload = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(payload.error || 'Failed to delete tag');
}

// Add or remove a set of tags across many clients at once. 'add' preserves each
// client's existing tags (inserts only missing pairs); used by the Select flow.
export async function bulkTagClients(
  clientIds: string[],
  tagIds: string[],
  action: 'add' | 'remove' = 'add',
): Promise<{ ok: boolean; updated: number }> {
  const res = await signedFetch(`${PREFIX}/clients/tags/bulk`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ clientIds, tagIds, action }),
  });
  const payload = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(payload.error || 'Failed to update client tags');
  return payload;
}

export async function fetchCampaigns(): Promise<{
  campaigns: Campaign[];
  grouped: Record<string, Campaign[]>;
}> {
  if (__demoMode) return demoCampaigns();
  const res = await signedFetch(`${PREFIX}/campaigns`);
  if (!res.ok) throw new Error('Failed to fetch campaigns');
  return res.json();
}

export async function createCampaign(data: {
  Name: string;
  days: CampaignDayInput[];
  scheduleType?: CampaignScheduleType;
}): Promise<Campaign[]> {
  const res = await signedFetch(`${PREFIX}/campaigns`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const payload = await res.json().catch(() => ({}));
    throw new Error(payload.error || 'Failed to create campaign');
  }
  return res.json();
}

export interface LatestPickerSalesUpdate {
  imgUrl: string | null;
  startDate: string | null;
  endDate: string | null;
}

export async function fetchLatestPickerSalesUpdate(): Promise<LatestPickerSalesUpdate> {
  if (__demoMode) return demoLatestPickerSalesUpdate();
  const res = await signedFetch(`${PREFIX}/picker-sales-update/latest`);
  if (!res.ok) throw new Error('Failed to fetch weekly sales update image');
  return res.json();
}

/** Upload failures: the server's reason, or a size message when only the status says 413. */
async function uploadErrorMessage(res: Response): Promise<string> {
  const payload = await res.json().catch(() => ({} as { error?: string }));
  if (payload.error) return payload.error;
  if (res.status === 413) return 'File is too large to upload. Uploads are capped at 16 MB.';
  return `Upload failed (HTTP ${res.status}). Try again.`;
}

export async function uploadConnectorMedia(slot: string, dataUrl: string): Promise<{ url: string }> {
  const res = await signedFetch(`${PREFIX}/media/upload`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ slot, dataUrl }),
  });
  if (!res.ok) throw new Error(await uploadErrorMessage(res));
  return res.json();
}

export type ConnectorLibraryMediaType = 'image' | 'video' | 'document' | 'voice';

export interface ConnectorLibraryMedia {
  id: string;
  name: string;
  mediaType: string;
  url: string;
  mimeType: string | null;
  sizeBytes: string | null;
  firstSentAt: string | null;
  canDelete: boolean;
  openCount: number;
  uniqueClientCount: number;
  sourceEdmId?: string | null;
  sourceUnavailable?: boolean;
  sourceUnavailableReason?: string | null;
  createdAt: string;
  updatedAt: string;
}

export type PeMediaKind = 'edm' | 'ebook' | 'periodic';

export type PeSearchItem = {
  edmId: string;
  name: string;
  kind: PeMediaKind;
  category: string | null;
  thumbnailUrl: string | null;
  projectNames: string[];
  outputType: string | null;
};

export async function searchPeMedia(params: {
  kind: PeMediaKind;
  q?: string;
  projectId?: string;
}): Promise<PeSearchItem[]> {
  if (__demoMode) return [];
  const qs = new URLSearchParams({ kind: params.kind });
  if (params.q?.trim()) qs.set('q', params.q.trim());
  if (params.projectId?.trim()) qs.set('projectId', params.projectId.trim());
  const res = await signedFetch(`${PREFIX}/media/project-essential/search?${qs}`);
  if (!res.ok) throw new Error('Failed to search Project Essential');
  const payload = await res.json();
  return payload.items || [];
}

export async function searchPeProjects(q?: string): Promise<Array<{ id: string; name: string }>> {
  if (__demoMode) return [];
  const qs = new URLSearchParams();
  if (q?.trim()) qs.set('q', q.trim());
  const res = await signedFetch(`${PREFIX}/media/project-essential/projects${qs.toString() ? `?${qs}` : ''}`);
  if (!res.ok) throw new Error('Failed to search projects');
  const payload = await res.json();
  return payload.items || [];
}

export async function importLibraryMediaFromPe(edmId: string): Promise<ConnectorLibraryMedia> {
  const res = await signedFetch(`${PREFIX}/media/library/from-project-essential`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ edmId }),
  });
  const payload = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(payload.error || 'Failed to import from Project Essential');
  return payload;
}

export type ShareWhatsAppLinkResult = {
  trackUrl: string;
  mediaId: string;
  clientName: string;
  clientMobile: string;
  agentName: string;
};

/** Import PE asset (if needed) and mint a tracked media link for WhatsApp share. */
export async function createShareWhatsAppLink(data: {
  edmId: string;
  clientId: string;
}): Promise<ShareWhatsAppLinkResult> {
  const res = await signedFetch(`${PREFIX}/media/share-whatsapp-link`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  const payload = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(payload.error || 'Failed to create WhatsApp share link');
  return payload;
}

export async function fetchMediaLibrary(params?: {
  type?: ConnectorLibraryMediaType | '';
  q?: string;
  limit?: number;
  offset?: number;
}): Promise<{ items: ConnectorLibraryMedia[]; total: number }> {
  if (__demoMode) return { items: [], total: 0 };
  const qs = new URLSearchParams();
  if (params?.type) qs.set('type', params.type);
  if (params?.q?.trim()) qs.set('q', params.q.trim());
  if (params?.limit != null) qs.set('limit', String(params.limit));
  if (params?.offset != null) qs.set('offset', String(params.offset));
  const res = await signedFetch(`${PREFIX}/media/library${qs.toString() ? `?${qs}` : ''}`);
  if (!res.ok) throw new Error('Failed to list media');
  return res.json();
}

export async function uploadLibraryMedia(data: {
  dataUrl: string;
  mediaType: ConnectorLibraryMediaType;
  name?: string;
}): Promise<ConnectorLibraryMedia> {
  const res = await signedFetch(`${PREFIX}/media/library`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(await uploadErrorMessage(res));
  return res.json();
}

export async function renameLibraryMedia(id: string, name: string): Promise<{ id: string; name: string }> {
  const res = await signedFetch(`${PREFIX}/media/library/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name }),
  });
  const payload = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(payload.error || 'Failed to rename media');
  return payload;
}

export async function deleteLibraryMedia(id: string): Promise<void> {
  const res = await signedFetch(`${PREFIX}/media/library/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
  if (!res.ok) {
    const payload = await res.json().catch(() => ({}));
    throw new Error(payload.error || 'Failed to delete media');
  }
}

export type LibraryMediaOpen = {
  clientId: string;
  clientName: string;
  campaignName: string | null;
  viewedAt: string;
};

export type LibraryMediaCampaignRate = {
  campaignId: string | null;
  campaignName: string;
  uniqueOpeners: number;
  clientsSent: number;
  openRate: number;
};

export type LibraryMediaOpensSummary = {
  totalOpens: number;
  uniqueOpeners: number;
  clientsSent: number;
  campaignsUsing: number;
  openRate: number;
};

export type LibraryMediaTopClient = {
  clientId: string;
  clientName: string;
  openCount: number;
  lastViewedAt: string;
};

export async function fetchLibraryMediaOpens(
  id: string,
): Promise<{
  media: { id: string; name: string };
  opens: LibraryMediaOpen[];
  campaignRates: LibraryMediaCampaignRate[];
  summary: LibraryMediaOpensSummary;
  topClients: LibraryMediaTopClient[];
}> {
  if (__demoMode) {
    return {
      media: { id, name: 'Demo media' },
      opens: [],
      campaignRates: [],
      summary: { totalOpens: 0, uniqueOpeners: 0, clientsSent: 0, campaignsUsing: 0, openRate: 0 },
      topClients: [],
    };
  }
  const res = await signedFetch(`${PREFIX}/media/library/${encodeURIComponent(id)}/opens`);
  if (!res.ok) {
    const payload = await res.json().catch(() => ({}));
    throw new Error(payload.error || 'Failed to load media opens');
  }
  return res.json();
}

export async function fetchAgentSettings(): Promise<AgentSettings> {
  if (__demoMode) return demoAgentSettings();
  const res = await signedFetch(`${PREFIX}/agent-settings`);
  if (!res.ok) throw new Error('Failed to fetch settings');
  return res.json();
}

export async function updateAgentSettings(data: {
  calendlyEventTypeUri?: string | null;
  engagementStartDayOffset?: number;
  engagementActive?: boolean;
  engagementMessagesPerHour?: number;
  whatsAppCloudDailyBudgetSgd?: number | null;
}): Promise<AgentSettings> {
  const res = await signedFetch(`${PREFIX}/agent-settings`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Failed to update settings');
  return res.json();
}

export async function connectCalendly(apiKey: string): Promise<{ user: CalendlyUser; eventTypes: CalendlyEventType[] }> {
  const res = await signedFetch(`${PREFIX}/calendly/connect`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ apiKey }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to connect Calendly');
  }
  return res.json();
}

export async function disconnectCalendly(): Promise<void> {
  await signedFetch(`${PREFIX}/calendly`, { method: 'DELETE' });
}

export async function fetchCalendlyEventTypes(): Promise<CalendlyEventType[]> {
  if (__demoMode) return demoCalendlyEventTypes();
  const res = await signedFetch(`${PREFIX}/calendly/event-types`);
  if (!res.ok) throw new Error('Failed to fetch event types');
  const data = await res.json();
  return data.eventTypes || [];
}

// ─── WhatsApp pairing ───
export interface WhatsAppState {
  status: 'initializing' | 'pairing' | 'authenticated' | 'connected' | 'disconnected' | 'error';
  qrDataUrl: string | null;
  phone: string | null;
  lastError?: string | null;
  mock?: boolean;
}

/**
 * Whether Settings/Campaigns should call live `/whatsapp/status` on page load.
 * Skip on local CRA and the hosted dev project (`transformation-dev.web.app`) —
 * the bridge is often unreachable there and would stall the spinner.
 * Pairing-stream status polling is separate and always runs during QR scan.
 */
export function shouldQueryLiveWhatsAppBridge(hostname?: string): boolean {
  const host = (hostname ?? (typeof window !== 'undefined' ? window.location.hostname : '')).toLowerCase();
  if (!host) return false;
  if (host === 'localhost' || host === '127.0.0.1' || host === '::1') return false;
  if (host === 'transformation-dev.web.app' || host.endsWith('.transformation-dev.web.app')) return false;
  return true;
}

export function openWhatsAppPairingStream(onState: (state: WhatsAppState) => void, onError: (msg: string) => void): () => void {
  let aborted = false;
  let controller: AbortController | null = new AbortController();
  let pollTimer: ReturnType<typeof setInterval> | null = null;

  // Poll /whatsapp/status as a reliable fallback for environments where SSE is
  // buffered (e.g. Firebase Hosting rewrites buffer Cloud Function responses).
  // Always on during pairing — hostname does not gate this path.
  const startPolling = () => {
    if (pollTimer) return;
    const poll = async () => {
      if (aborted) return;
      try {
        const r = await signedFetch(`${PREFIX}/whatsapp/status`, { cache: 'no-store' });
        if (r.ok) {
          const data: WhatsAppState = await r.json();
          if (data?.status) onState(data);
          if (data?.status === 'connected' || data?.status === 'error') {
            if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
          }
        }
      } catch {
        // ignore transient poll errors
      }
    };
    poll();
    pollTimer = setInterval(poll, 2000);
  };

  const connect = async () => {
    if (aborted) return;

    try {
      // Start polling before opening the stream. On true production hosts the
      // hosting / functions path can buffer SSE headers, which leaves fetch() pending.
      startPolling();

      const response = await signedFetch(`${PREFIX}/whatsapp/pair/stream`, {
        signal: controller!.signal,
      });

      if (!response.ok) {
        if (response.status === 401) {
          onError('Authentication required');
          return;
        }
        onError(`HTTP ${response.status}`);
        return;
      }

      if (!response.body) {
        onError('No response body');
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (!aborted) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Parse complete SSE frames delimited by blank lines (\n\n).
        // Accumulating until \n\n ensures large payloads (e.g. QR data URLs)
        // that span multiple TCP chunks are never split mid-frame.
        let idx: number;
        while ((idx = buffer.indexOf('\n\n')) !== -1) {
          const frame = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);

          let eventName = 'message';
          const dataLines: string[] = [];
          for (const rawLine of frame.split('\n')) {
            const line = rawLine.trimEnd();
            if (line.startsWith('event:')) {
              eventName = line.slice(6).trim();
            } else if (line.startsWith('data:')) {
              dataLines.push(line.slice(5).trimStart());
            }
          }

          if (dataLines.length === 0) continue;
          const dataStr = dataLines.join('\n');

          if (eventName === 'state') {
            try {
              const data = JSON.parse(dataStr);
              onState(data);
            } catch (e) {
              console.warn('Failed to parse SSE data:', e);
            }
          } else if (eventName === 'error') {
            try {
              const data = JSON.parse(dataStr);
              onError(data.message || 'Stream error');
            } catch (e) {
              onError('Stream error');
            }
          }
        }
      }
    } catch (err) {
      if (!aborted && err instanceof Error) {
        if (err.name === 'AbortError') {
          // Connection was intentionally closed
          return;
        }
        // SSE failed — polling keeps state updates flowing on hosts that allow it
        startPolling();
        onError(err.message || 'Connection error');
      }
    }
  };

  connect();

  return () => {
    aborted = true;
    if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
    if (controller) {
      controller.abort();
      controller = null;
    }
  };
}

export async function disconnectWhatsApp(): Promise<void> {
  const res = await signedFetch(`${PREFIX}/whatsapp/disconnect`, { method: 'POST' });
  if (!res.ok) throw new Error('Failed to disconnect WhatsApp');
}

export interface WhatsAppLiveStatus {
  status: WhatsAppState['status'];
  phone: string | null;
  qrDataUrl: string | null;
}

export async function fetchWhatsAppStatus(): Promise<WhatsAppLiveStatus> {
  if (__demoMode) return demoWhatsAppStatus();
  const res = await signedFetch(`${PREFIX}/whatsapp/status`);
  if (!res.ok) throw new Error('Failed to fetch WhatsApp status');
  return res.json();
}

// ─── Official WhatsApp API (Meta Cloud API) ───
// The agent connects their existing WhatsApp Business app number through Meta
// Embedded Signup in Coexistence mode: the number keeps working in the app
// while we send templates from it on their behalf.

/** Which transport the agent's engagement sends use. Only one is ever active. */
export type WhatsAppProvider = 'bridge' | 'cloud';

/** Meta's template review states, plus null before a template is submitted. */
export type TemplateApprovalStatus =
  | 'PENDING'
  | 'APPROVED'
  | 'REJECTED'
  | 'PAUSED'
  | 'DISABLED'
  | 'PENDING_DELETION'
  | 'IN_APPEAL'
  | string;

export interface WhatsAppCloudAccount {
  connected: boolean;
  status: 'pending' | 'connected' | 'disconnected' | 'error' | string;
  statusDetail?: string | null;
  wabaId?: string;
  phoneNumberId?: string;
  displayPhoneNumber?: string | null;
  verifiedName?: string | null;
  isCoexistence?: boolean;
  platformType?: string | null;
  qualityRating?: string | null;
  connectedAt?: string | null;
  disconnectedAt?: string | null;
  disconnectReason?: string | null;
  /**
   * Whether Meta reports a primary funding ID on this WABA.
   * false = confirmed missing; null = unknown (often Tech Provider / not verifiable).
   */
  paymentMethodLinked?: boolean | null;
  /**
   * false when Meta refuses primary_funding_id (requires Business Solution Provider).
   * UI should still prompt the agent to set up billing manually.
   */
  paymentMethodVerifiable?: boolean;
}

export interface EngagementTemplateState {
  dayNumber: number;
  status: TemplateApprovalStatus | null;
  templateName: string | null;
  syncedAt: string | null;
  /** Earliest time Meta will accept another edit of this template. */
  nextEditableAt: string | null;
  editLocked: boolean;
}

export type WhatsAppCloudAccountSource = 'personal' | 'company';

export interface WhatsAppCloudDailyBudget {
  limitSgd: number | null;
  sentToday: number;
  estimatedSpendSgd: number;
  messagesLeft: number | null;
  paused: boolean;
}

export interface WhatsAppCloudStatus {
  /** False when the Meta app credentials aren't configured on the server. */
  available: boolean;
  appId: string | null;
  configId: string | null;
  provider: WhatsAppProvider;
  account: WhatsAppCloudAccount;
  /** Which WABA sends use when provider is cloud. */
  accountSource?: WhatsAppCloudAccountSource;
  /** True only for the gated admin CEA (HT250401). */
  canSwitchCloudAccountSource?: boolean;
  companyAccountConnected?: boolean;
  dailyBudget?: WhatsAppCloudDailyBudget;
  templates: EngagementTemplateState[];
}

export type CloudSpendDay = {
  dayNumber: number;
  templateId: string | null;
  cost: number;
};

export type CloudSpendCampaign = {
  name: string;
  totalCost: number;
  days: CloudSpendDay[];
  firstSentAt: string | null;
};

export type CloudSpendResponse = {
  currency: 'SGD';
  lookbackDays: number;
  lookbackClamped: boolean;
  /** @deprecated Local spend no longer uses Meta insights; kept for older responses. */
  insightsUnavailable?: boolean;
  insightsUnavailableReason?: string | null;
  /** True when costs are rate × Official API send logs (not Meta billed). */
  estimated?: boolean;
  estimateRateSgd?: number;
  engagement: CloudSpendCampaign;
  campaigns: CloudSpendCampaign[];
  /** Manual sends of Campaigns → Message Templates, keyed by local template id. */
  messageTemplates?: Array<{ templateId: string; sendCount: number; cost: number }>;
  overall: {
    totalCost: number;
    daily: Array<{ date: string; cost: number }>;
  };
};

export async function fetchWhatsAppCloudSpend(): Promise<CloudSpendResponse> {
  const res = await signedFetch(`${PREFIX}/whatsapp-cloud/spend`);
  if (!res.ok) {
    throw new Error((await res.json().catch(() => ({}))).error || 'Failed to load Official API spend');
  }
  return res.json();
}

export interface TemplateSyncDayResult {
  dayNumber: number;
  templateName: string;
  status: TemplateApprovalStatus | null;
  action: 'created' | 'updated' | 'unchanged' | 'cooldown' | 'failed';
  error?: string;
  nextEditableAt?: string;
}

export interface TemplateSyncResponse {
  days: TemplateSyncDayResult[];
  templates: EngagementTemplateState[];
}

export async function fetchWhatsAppCloudStatus(): Promise<WhatsAppCloudStatus> {
  if (__demoMode) return demoWhatsAppCloudStatus();
  const res = await signedFetch(`${PREFIX}/whatsapp-cloud/status`);
  if (!res.ok) throw new Error('Failed to fetch WhatsApp API status');
  return res.json();
}

export async function onboardWhatsAppCloud(data: {
  code: string;
  wabaId: string;
  phoneNumberId: string;
  businessId?: string;
}): Promise<{ account: WhatsAppCloudAccount; templates: EngagementTemplateState[] }> {
  const res = await signedFetch(`${PREFIX}/whatsapp-cloud/onboard`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Failed to connect WhatsApp Business account');
  return res.json();
}

export async function refreshWhatsAppCloud(): Promise<{
  account: WhatsAppCloudAccount;
  templates: EngagementTemplateState[];
}> {
  const res = await signedFetch(`${PREFIX}/whatsapp-cloud/refresh`, { method: 'POST' });
  if (!res.ok) throw new Error('Failed to refresh WhatsApp API account');
  return res.json();
}

export async function disconnectWhatsAppCloud(): Promise<{
  account: WhatsAppCloudAccount;
  provider: WhatsAppProvider;
}> {
  const res = await signedFetch(`${PREFIX}/whatsapp-cloud/disconnect`, { method: 'POST' });
  if (!res.ok) throw new Error('Failed to disconnect WhatsApp API account');
  return res.json();
}

export async function setWhatsAppProvider(provider: WhatsAppProvider): Promise<{ provider: WhatsAppProvider }> {
  const res = await signedFetch(`${PREFIX}/whatsapp-cloud/provider`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ provider }),
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Failed to switch WhatsApp provider');
  const data = await res.json() as { provider: WhatsAppProvider };
  // Setup checklist day rows differ for bridge vs Official API (Meta APPROVED
  // is only required on cloud). Tell the navbar to re-fetch without navigating.
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('sc-setup-status-changed', { detail: { provider: data.provider } }));
  }
  return data;
}

/** Personal vs company Official API WABA — gated to one admin CEA server-side. */
export async function setWhatsAppCloudAccountSource(
  source: WhatsAppCloudAccountSource,
): Promise<{
  accountSource: WhatsAppCloudAccountSource;
  account: WhatsAppCloudAccount;
  provider: WhatsAppProvider;
}> {
  const res = await signedFetch(`${PREFIX}/whatsapp-cloud/account-source`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ source }),
  });
  if (!res.ok) {
    throw new Error((await res.json().catch(() => ({}))).error || 'Failed to switch Official API account');
  }
  return res.json();
}

/** Submit one engagement day to Meta, or every active day when `dayNumber` is omitted. */
export async function syncEngagementTemplates(dayNumber?: number): Promise<TemplateSyncResponse> {
  const res = await signedFetch(`${PREFIX}/whatsapp-cloud/templates/sync`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(dayNumber != null ? { dayNumber } : {}),
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Failed to sync templates with Meta');
  return res.json();
}

/** Sync custom-campaign days to Meta after Save (skips unchanged content hashes). */
export async function syncCustomCampaignTemplates(data: {
  campaignName: string;
  dayNumber?: number;
}): Promise<{ days: TemplateSyncDayResult[] }> {
  const res = await signedFetch(`${PREFIX}/whatsapp-cloud/templates/sync-custom`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    throw new Error((await res.json().catch(() => ({}))).error || 'Failed to sync custom campaign templates with Meta');
  }
  return res.json();
}

// ─── Meta Embedded Signup ───

type FacebookSdk = {
  init: (options: Record<string, unknown>) => void;
  login: (
    callback: (response: { authResponse?: { code?: string } | null; status?: string }) => void,
    options: Record<string, unknown>,
  ) => void;
};

declare global {
  interface Window {
    FB?: FacebookSdk;
    fbAsyncInit?: () => void;
  }
}

const FB_SDK_SCRIPT_ID = 'facebook-jssdk';

/** Load and initialise the Facebook JS SDK once per page. */
function loadFacebookSdk(appId: string): Promise<FacebookSdk> {
  return new Promise((resolve, reject) => {
    if (window.FB) {
      resolve(window.FB);
      return;
    }

    window.fbAsyncInit = () => {
      if (!window.FB) {
        reject(new Error('Facebook SDK failed to initialise'));
        return;
      }
      window.FB.init({ appId, autoLogAppEvents: true, xfbml: true, version: 'v21.0' });
      resolve(window.FB);
    };

    if (document.getElementById(FB_SDK_SCRIPT_ID)) return;
    const script = document.createElement('script');
    script.id = FB_SDK_SCRIPT_ID;
    script.src = 'https://connect.facebook.net/en_US/sdk.js';
    script.async = true;
    script.defer = true;
    script.crossOrigin = 'anonymous';
    script.onerror = () => reject(new Error('Could not load the Facebook SDK'));
    document.body.appendChild(script);
  });
}

export interface EmbeddedSignupResult {
  code: string;
  wabaId: string;
  phoneNumberId: string;
  businessId?: string;
}

/**
 * Launch Meta Embedded Signup and resolve with the assets needed to onboard.
 *
 * `featureType: 'whatsapp_business_app_onboarding'` is what puts the flow into
 * Coexistence mode, so the agent is offered their existing WhatsApp Business
 * app number instead of being asked to migrate or buy a new one.
 *
 * Two things must both arrive: the asset IDs come through a `window` message
 * event, and the exchangeable code comes through the FB.login callback. They
 * race, so we wait for both before resolving.
 */
export function launchWhatsAppEmbeddedSignup(params: {
  appId: string;
  configId: string;
}): Promise<EmbeddedSignupResult> {
  return new Promise((resolve, reject) => {
    let assets: { wabaId: string; phoneNumberId: string; businessId?: string } | null = null;
    let code: string | null = null;
    let settled = false;

    const finish = () => {
      if (settled || !assets || !code) return;
      settled = true;
      window.removeEventListener('message', onMessage);
      resolve({ ...assets, code });
    };

    const fail = (message: string) => {
      if (settled) return;
      settled = true;
      window.removeEventListener('message', onMessage);
      reject(new Error(message));
    };

    const onMessage = (event: MessageEvent) => {
      if (!/^https:\/\/www\.facebook\.com$/.test(event.origin)
        && !/^https:\/\/web\.facebook\.com$/.test(event.origin)) {
        return;
      }
      let data: Record<string, unknown>;
      try {
        data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
      } catch {
        return;
      }
      if (data?.type !== 'WA_EMBEDDED_SIGNUP') return;

      const payload = (data.data || {}) as Record<string, string>;
      if (data.event === 'FINISH' || data.event === 'FINISH_ONLY_WABA'
        || data.event === 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING') {
        assets = {
          wabaId: payload.waba_id,
          phoneNumberId: payload.phone_number_id,
          businessId: payload.business_id,
        };
        finish();
      } else if (data.event === 'CANCEL') {
        fail(payload.current_step
          ? `Setup was cancelled at the "${payload.current_step}" step.`
          : 'Setup was cancelled.');
      } else if (data.event === 'ERROR') {
        fail(payload.error_message || 'Meta reported an error during setup.');
      }
    };

    window.addEventListener('message', onMessage);

    loadFacebookSdk(params.appId)
      .then((fb) => {
        fb.login(
          (response) => {
            if (response?.authResponse?.code) {
              code = response.authResponse.code;
              finish();
              return;
            }
            // No code means the popup closed without completing the flow. The
            // message listener may already have reported a specific reason.
            fail('WhatsApp setup did not complete.');
          },
          {
            config_id: params.configId,
            response_type: 'code',
            override_default_response_type: true,
            extras: {
              setup: {},
              featureType: 'whatsapp_business_app_onboarding',
              sessionInfoVersion: '3',
            },
          },
        );
      })
      .catch((err: Error) => fail(err.message));
  });
}

export interface TestSendResult {
  ok: boolean;
  messageId?: string;
  previewOnly?: boolean;
  email?: { to: string | null; subject: string };
  rendered?: {
    body?: string;
    mediaUrl?: string | null;
    caption?: string | null;
    asVoice?: boolean;
    appendedLinks?: string[];
  };
  error?: string;
}

export interface TestSendInput {
  channel: 'whatsapp' | 'email';
  dayNumber: number;
  campaignName?: string;
  clientId?: string;
  mobile?: string;
  email?: string;
}

export async function testSendEngagement(data: TestSendInput): Promise<TestSendResult> {
  const res = await signedFetch(`${PREFIX}/engagement/test-send`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  const payload = await res.json().catch(() => ({}));
  if (!res.ok) return { ok: false, error: payload.error || 'Test send failed', rendered: payload.rendered };
  return {
    ok: true,
    messageId: payload.messageId,
    previewOnly: payload.previewOnly,
    email: payload.email,
    rendered: payload.rendered,
  };
}

export async function enrollClientInCampaign(campaignName: string, clientId: string): Promise<CampaignExecution[]> {
  const res = await signedFetch(`${PREFIX}/campaigns/${encodeURIComponent(campaignName)}/enroll/${clientId}`, {
    method: 'POST',
  });
  if (!res.ok) throw new Error('Failed to enroll client');
  return res.json();
}

// ─── Hyping projects (Super Picker "Sitemaps" list) ───
// Mirrors the project list shown in Super Picker's Sitemaps tab so an agent can
// tag which project they were hyping when capturing a client.
export interface HypingProjectOption {
  projectId: string;
  projectName: string;
  district?: string;
  region?: string;
  area?: string;
  location?: string;
  topDate?: string;
  upcoming?: boolean;
  /** Project pin coordinates (Project.MapLatitude/MapLongitude) — used by the Clients map. */
  mapLatitude?: number | null;
  mapLongitude?: number | null;
}

export interface HypingProjectRef {
  id: string;
  name: string;
}

/** Maximum number of projects an agent can tag a client with. */
export const MAX_HYPING_PROJECTS = 3;

/**
 * Read a client's hyped projects from the HypingProjects array attached by the
 * read endpoints, falling back to the legacy single HypingProjectId/HypingProject
 * pair so older records (and partial payloads) still render.
 */
export function parseHypingProjects(
  client: Pick<Client, 'HypingProjects' | 'HypingProjectId' | 'HypingProject'>
): HypingProjectRef[] {
  // Accept the new array shape or a legacy JSON-string payload.
  let list: unknown[] | null = null;
  if (Array.isArray(client.HypingProjects)) {
    list = client.HypingProjects;
  } else if (typeof client.HypingProjects === 'string' && client.HypingProjects.trim()) {
    try {
      const parsed = JSON.parse(client.HypingProjects);
      if (Array.isArray(parsed)) list = parsed;
    } catch { /* malformed JSON — fall through to legacy fields */ }
  }
  if (list) {
    const refs = list
      .map((p) => (p && typeof p === 'object' ? (p as { id?: unknown; name?: unknown }) : null))
      .filter((p): p is { id?: unknown; name?: unknown } =>
        !!p && typeof p.name === 'string' && p.name.trim().length > 0)
      .map((p) => ({ id: String(p.id ?? ''), name: String(p.name).trim() }));
    if (refs.length > 0) return refs;
  }
  if (client.HypingProject && client.HypingProject.trim()) {
    return [{ id: client.HypingProjectId || '', name: client.HypingProject.trim() }];
  }
  return [];
}

export async function fetchHypingProjects(): Promise<HypingProjectOption[]> {
  if (__demoMode) return DEMO_HYPING_PROJECTS;
  const res = await signedFetch('/api/projects/top-viewing-site-plans');
  if (!res.ok) throw new Error('Failed to fetch projects');
  const data = await res.json();
  return Array.isArray(data?.sitePlans) ? data.sitePlans : [];
}

export async function fetchNewLaunches(search?: string): Promise<Array<Record<string, unknown>>> {
  const sp = new URLSearchParams();
  if (search) sp.set('search', search);
  const res = await signedFetch(`${PREFIX}/new-launches?${sp.toString()}`);
  if (!res.ok) throw new Error('Failed to fetch launches');
  return res.json();
}

export async function fetchDashboardSummary(adminMode?: boolean): Promise<DashboardSummary> {
  if (__demoMode) return demoDashboardSummary();
  const res = await signedFetch(`${PREFIX}/dashboard/summary${companyScopeQuery(adminMode)}`);
  if (!res.ok) throw new Error('Failed to fetch dashboard');
  return res.json();
}

export async function fetchAgingData(adminMode?: boolean): Promise<AgingData> {
  if (__demoMode) return demoAging();
  const res = await signedFetch(`${PREFIX}/dashboard/aging${companyScopeQuery(adminMode)}`);
  if (!res.ok) throw new Error('Failed to fetch aging');
  return res.json();
}

// ─── Dashboard: today's 7-Day Engagement send summary ───
export interface EngagementTodayExecution {
  executionId: string;
  dayNumber: number;
  status: 'pending' | 'sending' | 'sent' | 'failed' | 'skipped' | string;
  sentAt: string | null;
  lastError: string | null;
  updatedAt: string;
  clientId: string;
  clientName: string;
}

export interface EngagementTodaySummary {
  counts: { pending: number; sending: number; sent: number; failed: number; skipped: number };
  executions: EngagementTodayExecution[];
}

export async function fetchEngagementTodaySummary(adminMode?: boolean): Promise<EngagementTodaySummary> {
  if (__demoMode) return demoEngagementToday();
  const res = await signedFetch(`${PREFIX}/dashboard/engagement-today${companyScopeQuery(adminMode)}`);
  if (!res.ok) throw new Error('Failed to fetch engagement summary');
  return res.json();
}

// ─── Per-client 7-Day Engagement controls ───
export interface ClientEngagementProgress {
  EngagementActive: boolean;
  lastSentDay: number | null;
  lastSentAt: string | null;
  executions: CampaignExecution[];
  campaign: Campaign[];
}

export async function setClientEngagementActive(clientId: string, active: boolean): Promise<{ ok: boolean; EngagementActive: boolean }> {
  const res = await signedFetch(`${PREFIX}/clients/${clientId}/engagement`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ active }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to update engagement');
  }
  return res.json();
}

export async function fetchClientEngagement(clientId: string): Promise<ClientEngagementProgress> {
  if (__demoMode) return demoClientEngagement(clientId);
  const res = await signedFetch(`${PREFIX}/clients/${clientId}/engagement`);
  if (!res.ok) throw new Error('Failed to fetch engagement progress');
  return res.json();
}

// — Per-client custom-campaign status / enrol / manual send (client detail) —
export interface ClientCampaignProgress {
  name: string;
  numDays: number;
  enrolled: boolean;
  /** Agent-level campaign Active flag (auto-send). Independent of enrol. */
  active?: boolean;
  executions: CampaignExecution[];
  lastSentDay: number;
  lastSentAt: string | null;
}

export async function fetchClientCampaigns(clientId: string): Promise<ClientCampaignProgress[]> {
  const res = await signedFetch(`${PREFIX}/clients/${clientId}/campaigns`);
  if (!res.ok) throw new Error('Failed to fetch client campaigns');
  return res.json();
}

export async function setClientCampaignEnrolled(clientId: string, name: string, active: boolean): Promise<{ ok: boolean; enrolled: boolean }> {
  const res = await signedFetch(`${PREFIX}/clients/${clientId}/campaigns/${encodeURIComponent(name)}/enroll`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ active }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to update enrollment');
  }
  return res.json();
}

export async function sendNextClientCampaignDay(clientId: string, name: string): Promise<SendNextEngagementResult> {
  const res = await signedFetch(`${PREFIX}/clients/${clientId}/campaigns/${encodeURIComponent(name)}/send-now`, {
    method: 'POST',
  });
  const payload = await res.json().catch(() => ({}));
  if (!res.ok) return { ok: false, error: payload.error || 'Failed to send' };
  return { ok: true, dayNumber: payload.dayNumber, messageId: payload.messageId, rendered: payload.rendered };
}

export async function resetClientCampaign(
  clientId: string,
  name: string,
  nextDay: number = 1,
): Promise<{ ok: boolean; nextDay: number; lastSentDay: number }> {
  const res = await signedFetch(`${PREFIX}/clients/${clientId}/campaigns/${encodeURIComponent(name)}/reset`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ nextDay }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to reset campaign');
  }
  return res.json();
}

export interface SendNextEngagementResult {
  ok: boolean;
  dayNumber?: number;
  messageId?: string;
  rendered?: TestSendResult['rendered'];
  error?: string;
}

export async function resetClientEngagement(clientId: string, nextDay: number = 1): Promise<{ ok: boolean; nextDay: number; lastSentDay: number }> {
  const res = await signedFetch(`${PREFIX}/clients/${clientId}/engagement/reset`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ nextDay }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to reset engagement');
  }
  return res.json();
}

export async function sendNextEngagementDayApi(clientId: string): Promise<SendNextEngagementResult> {
  const res = await signedFetch(`${PREFIX}/clients/${clientId}/engagement/send-now`, {
    method: 'POST',
  });
  const payload = await res.json().catch(() => ({}));
  if (!res.ok) return { ok: false, error: payload.error || 'Failed to send' };
  return { ok: true, dayNumber: payload.dayNumber, messageId: payload.messageId, rendered: payload.rendered };
}

export async function fetchWATypeDistribution(adminMode?: boolean): Promise<Array<{ type: string; count: string }>> {
  if (__demoMode) return demoWATypeDistribution();
  const res = await signedFetch(`${PREFIX}/dashboard/wa-type-distribution${companyScopeQuery(adminMode)}`);
  if (!res.ok) throw new Error('Failed to fetch distribution');
  return res.json();
}

// ═══ Admin: system templates + per-CEA set-up (ADMIN role only) ═══
export interface SystemMessageTemplate {
  Id: string;
  LineageId?: string | null;
  Title: string;
  Body: string;
  Type: string;
  Channel: string;
  IsActive: boolean;
  CreatedAt: string;
  UpdatedAt: string;
  DefunctAt?: string | null;
}

export interface SystemCampaignTemplate {
  Id: string;
  LineageId?: string | null;
  Name: string;
  DayNumber: number;
  Channel: string;
  MessageTemplate: string;
  MediaUrl?: string | null;
  CtaLabel?: string | null;
  CtaUrl?: string | null;
  IsActive: boolean;
  CreatedAt: string;
  UpdatedAt: string;
  DefunctAt?: string | null;
}

export interface SetupResult {
  ok: boolean;
  cea: string;
  messagesCopied: number;
  messagesReplaced: number;
  campaignsCopied: number;
}

// — System message templates —
export async function fetchSystemMessageTemplates(includeHistory = false): Promise<SystemMessageTemplate[]> {
  const sp = new URLSearchParams();
  if (includeHistory) sp.set('includeHistory', 'true');
  const res = await signedFetch(`${PREFIX}/admin/system-templates/messages?${sp.toString()}`);
  if (!res.ok) throw new Error('Failed to fetch system message templates');
  return res.json();
}

export async function createSystemMessageTemplate(data: {
  Title: string;
  Body: string;
  Type: string;
  Channel: string;
}): Promise<SystemMessageTemplate> {
  const res = await signedFetch(`${PREFIX}/admin/system-templates/messages`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Failed to create system message template');
  return res.json();
}

export async function updateSystemMessageTemplate(
  id: string,
  data: Partial<Pick<SystemMessageTemplate, 'Title' | 'Body' | 'Type' | 'Channel'>>
): Promise<SystemMessageTemplate> {
  const res = await signedFetch(`${PREFIX}/admin/system-templates/messages/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Failed to update system message template');
  return res.json();
}

export async function deleteSystemMessageTemplate(id: string): Promise<void> {
  const res = await signedFetch(`${PREFIX}/admin/system-templates/messages/${id}`, { method: 'DELETE' });
  if (!res.ok) throw new Error('Failed to delete system message template');
}

// — System campaign templates —
export async function fetchSystemCampaignTemplates(includeHistory = false): Promise<{
  campaigns: SystemCampaignTemplate[];
  grouped: Record<string, SystemCampaignTemplate[]>;
}> {
  const sp = new URLSearchParams();
  if (includeHistory) sp.set('includeHistory', 'true');
  const res = await signedFetch(`${PREFIX}/admin/system-templates/campaigns?${sp.toString()}`);
  if (!res.ok) throw new Error('Failed to fetch system campaign templates');
  return res.json();
}

export async function createSystemCampaignTemplate(data: {
  Name: string;
  DayNumber: number;
  Channel: string;
  MessageTemplate?: string;
  MediaUrl?: string | null;
  CtaLabel?: string | null;
  CtaUrl?: string | null;
}): Promise<SystemCampaignTemplate> {
  const res = await signedFetch(`${PREFIX}/admin/system-templates/campaigns`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Failed to create system campaign template');
  return res.json();
}

export async function updateSystemCampaignTemplate(
  id: string,
  data: Partial<Pick<SystemCampaignTemplate, 'Name' | 'DayNumber' | 'Channel' | 'MessageTemplate' | 'MediaUrl' | 'CtaLabel' | 'CtaUrl'>>
): Promise<SystemCampaignTemplate> {
  const res = await signedFetch(`${PREFIX}/admin/system-templates/campaigns/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Failed to update system campaign template');
  return res.json();
}

export async function deleteSystemCampaignTemplate(id: string): Promise<void> {
  const res = await signedFetch(`${PREFIX}/admin/system-templates/campaigns/${id}`, { method: 'DELETE' });
  if (!res.ok) throw new Error('Failed to delete system campaign template');
}

// — Set up a CEA from the system templates —
export async function setupCeaFromSystemTemplates(cea: string): Promise<SetupResult> {
  const res = await signedFetch(`${PREFIX}/admin/setup`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ cea }),
  });
  const payload = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(payload.error || 'Failed to set up CEA');
  return payload;
}

// ═══ Custom campaigns (admin-authored, granted to agents by CEA) ═══
export interface CampaignDef {
  Id: string;
  Name: string;
  NumDays: number;
  IsActive: boolean;
  CreatedAt: string;
  UpdatedAt: string;
  /** The one agent this campaign is assigned to, if any. */
  ceaNumber: string | null;
}

export type CampaignScheduleType = 'daily' | 'one_time';

export interface MyCampaign {
  /** CampaignDef id — used when linking triggers to a campaign. */
  defId: string;
  name: string;
  numDays: number;
  active: boolean;
  /** 'daily' = sequential cadence; 'one_time' = each day fires on its RunDate. */
  scheduleType: CampaignScheduleType;
  /** When the campaign def was created. */
  createdAt?: string | null;
  /** Latest agent/def/grant touch — used to sort the campaign list. */
  updatedAt?: string | null;
  days: Campaign[];
}

// — Admin: campaign defs (ADMIN role only) —
export async function fetchCampaignDefs(): Promise<CampaignDef[]> {
  const res = await signedFetch(`${PREFIX}/admin/campaign-defs`);
  if (!res.ok) throw new Error('Failed to fetch campaigns');
  return res.json();
}

export async function createCampaignDef(data: { Name: string; NumDays: number; ceaNumber: string | null }): Promise<CampaignDef> {
  const res = await signedFetch(`${PREFIX}/admin/campaign-defs`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  const payload = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(payload.error || 'Failed to create campaign');
  return payload;
}

export async function updateCampaignDef(
  id: string,
  data: Partial<{ Name: string; NumDays: number; IsActive: boolean; ceaNumber: string | null }>,
): Promise<CampaignDef> {
  const res = await signedFetch(`${PREFIX}/admin/campaign-defs/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  const payload = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(payload.error || 'Failed to update campaign');
  return payload;
}

export async function deleteCampaignDef(id: string): Promise<void> {
  const res = await signedFetch(`${PREFIX}/admin/campaign-defs/${id}`, { method: 'DELETE' });
  if (!res.ok) throw new Error('Failed to delete campaign');
}

// — Agent: campaigns granted to me —
export async function fetchMyCampaigns(): Promise<MyCampaign[]> {
  const res = await signedFetch(`${PREFIX}/my-campaigns`);
  if (!res.ok) throw new Error('Failed to fetch campaigns');
  return res.json();
}

export async function setMyCampaignActive(name: string, active: boolean): Promise<{ ok: boolean; active: boolean }> {
  const res = await signedFetch(`${PREFIX}/my-campaigns/${encodeURIComponent(name)}/active`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ active }),
  });
  if (!res.ok) throw new Error('Failed to update campaign');
  return res.json();
}

/** Send one saved day of a custom campaign to any number, as a dry run. */
export async function testSendCustomCampaignDay(
  name: string,
  dayNumber: number,
  mobile: string,
): Promise<TestSendResult> {
  const res = await signedFetch(
    `${PREFIX}/my-campaigns/${encodeURIComponent(name)}/days/${dayNumber}/test-send`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ mobile }),
    },
  );
  const payload = await res.json().catch(() => ({}));
  if (!res.ok) return { ok: false, error: payload.error || 'Test send failed', rendered: payload.rendered };
  return { ok: true, messageId: payload.messageId, rendered: payload.rendered };
}

export async function createMyCampaign(data: { name: string; numDays: number }): Promise<{
  defId: string;
  name: string;
  numDays: number;
  active: boolean;
}> {
  const res = await signedFetch(`${PREFIX}/my-campaigns`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  const payload = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(payload.error || 'Failed to create campaign');
  return payload;
}

export async function updateMyCampaignStructure(
  name: string,
  data: { numDays?: number; removeDayNumber?: number },
): Promise<{ ok: boolean; numDays: number; name: string }> {
  const res = await signedFetch(`${PREFIX}/my-campaigns/${encodeURIComponent(name)}/structure`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  const payload = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(payload.error || 'Failed to update campaign days');
  return payload;
}

// — Per-campaign client enrollment (which clients receive this campaign) —
export async function fetchCampaignEnrollment(name: string): Promise<{ clientIds: string[] }> {
  const res = await signedFetch(`${PREFIX}/my-campaigns/${encodeURIComponent(name)}/enrollment`);
  if (!res.ok) throw new Error('Failed to fetch recipients');
  return res.json();
}

export async function setCampaignEnrollment(name: string, clientIds: string[]): Promise<{ ok: boolean; clientIds: string[] }> {
  const res = await signedFetch(`${PREFIX}/my-campaigns/${encodeURIComponent(name)}/enrollment`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ clientIds }),
  });
  if (!res.ok) throw new Error('Failed to update recipients');
  return res.json();
}

// ═══ Inbound WhatsApp auto-reply triggers ═══
// A trigger watches the agent's inbound replies for a phrase (matched on the
// bridge — message content never reaches the backend). On match the client is
// tagged and optionally enrolled into a custom campaign.
export interface InboundTrigger {
  Id: string;
  CEANumber: string;
  Phrase: string;
  TagId: string;
  CampaignDefId?: string | null;
  IsActive: boolean;
  CreatedAt: string;
  UpdatedAt: string;
}

export interface InboundTriggerInput {
  Phrase: string;
  TagId: string;
  CampaignDefId?: string | null;
  IsActive?: boolean;
}

export async function fetchInboundTriggers(): Promise<InboundTrigger[]> {
  const res = await signedFetch(`${PREFIX}/inbound-triggers`);
  if (!res.ok) throw new Error('Failed to fetch triggers');
  return res.json();
}

export async function createInboundTrigger(data: InboundTriggerInput): Promise<InboundTrigger> {
  const res = await signedFetch(`${PREFIX}/inbound-triggers`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  const payload = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(payload.error || 'Failed to create trigger');
  return payload;
}

export async function updateInboundTrigger(id: string, data: Partial<InboundTriggerInput>): Promise<InboundTrigger> {
  const res = await signedFetch(`${PREFIX}/inbound-triggers/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  const payload = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(payload.error || 'Failed to update trigger');
  return payload;
}

export async function deleteInboundTrigger(id: string): Promise<void> {
  const res = await signedFetch(`${PREFIX}/inbound-triggers/${id}`, { method: 'DELETE' });
  if (!res.ok) throw new Error('Failed to delete trigger');
}

// ═══ Setup readiness (login-time checklist) ═══
export interface SetupCheck {
  key: string;
  group: 'engagement' | 'integration' | string;
  label: string;
  ok: boolean;
  hint?: string;
}
export interface SetupStatus {
  ready: boolean;
  checks: SetupCheck[];
}

export async function fetchSetupStatus(): Promise<SetupStatus> {
  if (__demoMode) return { ready: true, checks: [] };
  const res = await signedFetch(`${PREFIX}/setup-status`);
  if (!res.ok) throw new Error('Failed to fetch setup status');
  return res.json();
}

// ═══ Terms and conditions ═══

export interface ConnectorTerm {
  id: number;
  body: string;
  sortOrder: number;
}

export interface ConnectorTermsSnapshot {
  version: number;
  terms: ConnectorTerm[];
  /** When the live version was saved (ISO). */
  updatedAt: string | null;
}

export interface ConnectorTermsStatus extends ConnectorTermsSnapshot {
  mustAgree: boolean;
  /** Agreed to an earlier version that no longer counts — show "terms updated". */
  previouslyAgreed: boolean;
  /** When the version requiring re-agreement was saved (ISO). */
  requiredSince: string | null;
}

/** Thrown by agreeToTerms when the terms changed while the dialog was open. */
export class TermsChangedError extends Error {}

export async function fetchTermsStatus(): Promise<ConnectorTermsStatus> {
  if (__demoMode) {
    return { mustAgree: false, version: 0, terms: [], updatedAt: null, previouslyAgreed: false, requiredSince: null };
  }
  const res = await signedFetch(`${PREFIX}/terms/status`);
  if (!res.ok) throw new Error('Failed to load terms and conditions');
  return res.json();
}

export async function agreeToTerms(version: number, termIds: number[]): Promise<void> {
  const res = await signedFetch(`${PREFIX}/terms/agree`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ version, termIds }),
  });
  if (res.ok) return;
  const payload = await res.json().catch(() => ({} as { error?: string }));
  if (res.status === 409) throw new TermsChangedError(payload.error || 'The terms and conditions have changed.');
  throw new Error(payload.error || 'Failed to record your agreement. Please try again.');
}

export async function fetchAdminTerms(): Promise<ConnectorTermsSnapshot> {
  const res = await signedFetch(`${PREFIX}/admin/terms`);
  if (!res.ok) throw new Error('Failed to load terms and conditions');
  return res.json();
}

export async function saveAdminTerms(data: {
  terms: Array<{ body: string }>;
  requireReacceptance: boolean;
}): Promise<ConnectorTermsSnapshot> {
  const res = await signedFetch(`${PREFIX}/admin/terms`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  const payload = await res.json().catch(() => ({} as { error?: string }));
  if (!res.ok) throw new Error(payload.error || 'Failed to save terms and conditions');
  return payload as ConnectorTermsSnapshot;
}

// ═══ Access Codes ═══

export interface AccessCode {
  Id: string;
  Code: string;
  GeneratedBy: string;
  ExpiryDate: string;
  ActivatedBy?: string | null;
  ActivatedAt?: string | null;
  CreatedAt: string;
  UpdatedAt: string;
  DefunctAt?: string | null;
}

export interface AccessCodeWithNames extends AccessCode {
  GeneratedByName?: string | null;
  ActivatedByName?: string | null;
}

export interface AccessCodeQuota {
  Id: string;
  CEANumber: string;
  Quota: number;
  GrantedBy?: string | null;
  Note?: string | null;
  CreatedAt: string;
  UpdatedAt: string;
  UserName?: string | null;
}

export interface MyAccessCodesResponse {
  quota: number;
  used: number;
  codes: AccessCode[];
}

// — User: my access codes —
export async function fetchMyAccessCodes(): Promise<MyAccessCodesResponse> {
  const res = await signedFetch(`${PREFIX}/access-codes`);
  if (!res.ok) throw new Error('Failed to fetch access codes');
  return res.json();
}

export async function generateAccessCode(expiryDays: number): Promise<AccessCode> {
  const res = await signedFetch(`${PREFIX}/access-codes`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ expiryDays }),
  });
  const payload = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(payload.error || 'Failed to generate access code');
  return payload;
}

export async function deleteAccessCode(id: string): Promise<void> {
  const res = await signedFetch(`${PREFIX}/access-codes/${id}`, { method: 'DELETE' });
  const payload = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(payload.error || 'Failed to delete access code');
}

export async function fetchAccessCodeShareLink(id: string): Promise<{ link: string; code: string }> {
  const res = await signedFetch(`${PREFIX}/access-codes/${id}/share-link`);
  if (!res.ok) throw new Error('Failed to get share link');
  return res.json();
}

export async function sendAccessCodeWhatsApp(id: string, mobile: string, message?: string): Promise<{ ok: boolean; messageId?: string | null }> {
  const res = await signedFetch(`${PREFIX}/access-codes/${id}/send-whatsapp`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ mobile, ...(message ? { message } : {}) }),
  });
  const payload = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(payload.error || 'Failed to send WhatsApp');
  return payload;
}

export async function redeemAccessCode(code: string): Promise<{ ok: boolean; message: string }> {
  const res = await signedFetch(`${PREFIX}/access-code/redeem`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code }),
  });
  const payload = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(payload.error || 'Failed to redeem access code');
  return payload;
}

// — eNETS payment —
export interface PaymentInitiateResult {
  txnReq:         string;
  mac:            string;
  keyId:          string;
  gatewayUrl:     string;
  merchantTxnRef: string;
}

export async function fetchPaymentConfig(): Promise<{ amountCents: number; amountSgd: number }> {
  const res = await signedFetch(`${PREFIX}/payment/config`);
  const payload = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(payload.error || 'Failed to fetch payment config');
  return payload;
}

export async function initiatePayment(
  paymentMode: 'DD' | 'QR' | '',
  requestToken?: string,
): Promise<PaymentInitiateResult> {
  const res = await signedFetch(`${PREFIX}/payment/initiate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ paymentMode, requestToken }),
  });
  const payload = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(payload.error || 'Failed to initiate payment');
  return payload as PaymentInitiateResult;
}

export interface PaymentStatusResult {
  paymentStatus: string;
  paymentRequestStatus: string | null;
}

export async function fetchPaymentStatus(merchantTxnRef: string): Promise<PaymentStatusResult> {
  const res = await signedFetch(
    `${PREFIX}/payment/status/${encodeURIComponent(merchantTxnRef)}`,
    { cache: 'no-store' },
  );
  const payload = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(payload.error || 'Failed to fetch payment status');
  return payload as PaymentStatusResult;
}

export interface MyPaymentRequest {
  id: string;
  amountCents: number;
  status: string;
  token: string | null;
}

/** The logged-in user's outstanding (PENDING) payment request, or null. JWT-only. */
export async function fetchMyPaymentRequest(): Promise<MyPaymentRequest | null> {
  const res = await signedFetch(`${PREFIX}/payment-requests/mine`);
  if (!res.ok) return null;
  const payload = await res.json().catch(() => ({ request: null }));
  return (payload.request as MyPaymentRequest | null) ?? null;
}

/** Admin: send an activation payment request (email with CTA) to an agent. */
export async function sendPaymentRequest(cea: string): Promise<{ ok: boolean; emailSentTo: string | null }> {
  const res = await signedFetch(`${PREFIX}/admin/payment-requests`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ cea }),
  });
  const payload = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(payload.error || 'Failed to send payment request');
  return payload;
}

// — Admin: Manage Users —
export interface AdminUserRow {
  cea: string;
  name: string;
  mobile: string | null;
  email: string | null;
  role: string;
  clientCount: number;
  whatsappStatus: string;
  calendlyConnected: boolean;
  engagementActive: boolean;
  paymentStatus: 'pending' | 'paid' | 'none';
  paymentPaidAt: string | null;
  paymentTransactionId: string | null;
  paymentReceiptUrl: string | null;
  paymentReceiptStatus: string | null;
  /** Admin-only note about this user; never shown to the agent. */
  remarks: string | null;
}

export async function fetchAdminUsers(): Promise<AdminUserRow[]> {
  const res = await signedFetch(`${PREFIX}/admin/users`);
  if (!res.ok) throw new Error('Failed to load users');
  return res.json();
}

/** Save (or clear, with an empty string) the admin note against a user. */
export async function saveAdminUserRemarks(
  cea: string,
  remarks: string,
): Promise<{ ok: boolean; cea: string; remarks: string | null }> {
  const res = await signedFetch(`${PREFIX}/admin/users/${encodeURIComponent(cea)}/remarks`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ remarks }),
  });
  const payload = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(payload.error || 'Failed to save remarks');
  return payload;
}

// — Admin: paste-import ECB clients for an agent —

export interface ImportEcbContact {
  companyName?: string;
  name: string;
  mobile: string;
}

/**
 * Create the pasted contacts as clients under the agent (tagged "ECB"; the four
 * standard tags are created if missing). Creation is unconditional server-side:
 * a duplicate mobile still becomes a new client row.
 */
export async function adminImportEcbClients(
  cea: string,
  contacts: ImportEcbContact[],
): Promise<{ ok: boolean; created: number; skipped: number; tagsCreated: number }> {
  const res = await signedFetch(`${PREFIX}/admin/users/${encodeURIComponent(cea)}/import-ecb-clients`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ contacts }),
  });
  const payload = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(payload.error || 'Failed to import clients');
  return payload;
}

// — Admin: agent WhatsApp health + post-setup check-in —

/**
 * WhatsApp's "reach-out time-lock" state for one agent, asked of WhatsApp live.
 * While a lock is active the agent's outbound messages to contacts who haven't
 * messaged them first are rejected by WhatsApp (ack error 463).
 * `checked: false` means the agent's WhatsApp isn't paired, so nothing could be
 * asked — a setup problem rather than a restriction.
 */
export interface WhatsAppRestriction {
  cea: string;
  checked: boolean;
  connected: boolean;
  whatsappStatus?: string;
  restricted?: boolean;
  until?: string | null;
  enforcementType?: string | null;
  hint?: string | null;
}

export async function fetchAdminWhatsAppRestriction(cea: string): Promise<WhatsAppRestriction> {
  const res = await signedFetch(`${PREFIX}/admin/users/${encodeURIComponent(cea)}/whatsapp-restriction`);
  const payload = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(payload.error || 'Failed to check WhatsApp restriction');
  return payload;
}

export interface AdminCheckInSent {
  /** ISO timestamp of a check-in already sent to this agent. */
  sentAt: string;
  adminCea: string;
  adminName: string;
}

export interface AdminCheckInDraft {
  cea: string;
  name: string;
  mobile: string;
  /** Country-coded number for the wa.me link, e.g. "6598290620". */
  whatsappPhone: string;
  message: string;
  /** Previous check-ins to this agent, newest first (from the admin audit trail). */
  history: AdminCheckInSent[];
}

/** Preflight a check-in: resolves the recipient and the default message to review. */
export async function fetchAdminCheckInDraft(cea: string): Promise<AdminCheckInDraft> {
  const res = await signedFetch(`${PREFIX}/admin/users/${encodeURIComponent(cea)}/whatsapp-checkin`);
  const payload = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(payload.error || 'Failed to prepare check-in');
  return payload;
}

/**
 * Record that a check-in went out. The message itself is sent by the admin from
 * their own WhatsApp via the link, so this only writes the audit trail.
 */
export async function logAdminCheckIn(cea: string): Promise<{ ok: boolean; sentTo: string }> {
  // Body-less POST on purpose: an empty JSON object would be signed as "{}" here
  // but verified as "" server-side (Express parses it to {}), so it 401s. Also
  // non-redirecting — losing an audit row must not throw the admin to /sso.
  const res = await signedFetch(
    `${PREFIX}/admin/users/${encodeURIComponent(cea)}/whatsapp-checkin`,
    { method: 'POST' },
    false,
  );
  const payload = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(payload.error || 'Failed to record check-in');
  return payload;
}

// — Admin: impersonation ("login as user") + audit log —

/**
 * Replace the current admin session with one minted for the target agent.
 * On success the caller must full-reload the app so every view re-fetches as
 * the impersonated user.
 */
export async function impersonateUser(cea: string): Promise<void> {
  const res = await signedFetch(`${PREFIX}/admin/impersonate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ cea }),
  });
  const payload = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(payload.error || 'Failed to log in as user');
}

/** Restore the admin's own session from an impersonated one, then full-reload. */
export async function exitImpersonation(): Promise<void> {
  const res = await signedFetch(`${PREFIX}/impersonation/exit`, { method: 'POST' });
  const payload = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(payload.error || 'Failed to exit impersonation');
}

export interface AdminAuditLogRow {
  id: string;
  adminCea: string;
  adminName: string | null;
  targetCea: string | null;
  targetName: string | null;
  action: 'IMPERSONATE_START' | 'IMPERSONATE_END' | 'IMPERSONATED_REQUEST' | string;
  details: string | null;
  createdAt: string;
}

export async function fetchAdminAuditLogs(params?: { limit?: number; offset?: number }): Promise<{ total: number; logs: AdminAuditLogRow[] }> {
  const sp = new URLSearchParams();
  if (params?.limit) sp.set('limit', String(params.limit));
  if (params?.offset) sp.set('offset', String(params.offset));
  const res = await signedFetch(`${PREFIX}/admin/audit-logs?${sp.toString()}`);
  if (!res.ok) throw new Error('Failed to load audit logs');
  return res.json();
}

export interface EngagementTemplateRow {
  id: string;
  dayNumber: number;
  channel: string;
  messageTemplate: string;
  mediaUrl: string | null;
  imageUrl: string | null;
  voiceUrl: string | null;
  linkUrl: string | null;
  ctaLabel: string | null;
  ctaUrl: string | null;
  isActive: boolean;
}

/** Admin: an agent's 7-Day Engagement template (for the Manage Users viewer dialog). */
export async function fetchAdminUserEngagement(cea: string): Promise<EngagementTemplateRow[]> {
  const res = await signedFetch(`${PREFIX}/admin/users/${encodeURIComponent(cea)}/engagement`);
  if (!res.ok) throw new Error('Failed to load engagement template');
  return res.json();
}

export interface AdminAppointment {
  id: number;
  cea: string;
  agentName: string | null;
  photo: string | null;
  mobile: string | null;
  email: string | null;
  appointmentAt: string;
  durationMinutes: number;
  calendarMethod: string | null;
  attended: boolean | null;
  remarks: string | null;
  createdAt: string | null;
  whatsappUrl: string | null;
}

export interface AdminAgentProfile {
  cea: string;
  name: string;
  mobile: string;
  email: string;
}

export async function fetchAdminAgentProfile(cea: string): Promise<AdminAgentProfile | null> {
  const res = await signedFetch(`${PREFIX}/admin/agent-profile/${encodeURIComponent(cea)}`);
  if (res.status === 404) return null;
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || 'Failed to look up agent');
  return body as AdminAgentProfile;
}

export async function fetchAdminAppointments(includePast = false): Promise<AdminAppointment[]> {
  const res = await signedFetch(`${PREFIX}/admin/appointments${includePast ? '?includePast=1' : ''}`);
  if (!res.ok) throw new Error('Failed to load appointments');
  return res.json();
}

export async function updateAppointment(
  id: number,
  patch: { attended?: boolean | null; remarks?: string | null },
): Promise<void> {
  const res = await signedFetch(`${PREFIX}/admin/appointments/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error('Failed to update appointment');
}

export async function createAppointment(payload: {
  cea: string; agentName?: string; mobile?: string; email?: string;
  appointmentAt: string; durationMinutes?: number; remarks?: string;
}): Promise<{ emailSent: boolean; emailError: string | null }> {
  const res = await signedFetch(`${PREFIX}/admin/appointments`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || 'Failed to create appointment');
  return {
    emailSent: body.emailSent === true,
    emailError: typeof body.emailError === 'string' ? body.emailError : null,
  };
}

// — Admin: quota management —
export async function fetchAdminAccessCodeQuotas(): Promise<AccessCodeQuota[]> {
  const res = await signedFetch(`${PREFIX}/admin/access-code-quotas`);
  if (!res.ok) throw new Error('Failed to fetch quotas');
  return res.json();
}

export async function upsertAdminAccessCodeQuota(cea: string, quota: number, note?: string): Promise<AccessCodeQuota> {
  const res = await signedFetch(`${PREFIX}/admin/access-code-quotas/${encodeURIComponent(cea)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ quota, note: note ?? null }),
  });
  const payload = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(payload.error || 'Failed to save quota');
  return payload;
}

export async function deleteAdminAccessCodeQuota(cea: string): Promise<void> {
  const res = await signedFetch(`${PREFIX}/admin/access-code-quotas/${encodeURIComponent(cea)}`, { method: 'DELETE' });
  const payload = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(payload.error || 'Failed to delete quota');
}

// — Admin: all generated codes —
export async function fetchAdminAccessCodes(): Promise<AccessCodeWithNames[]> {
  const res = await signedFetch(`${PREFIX}/admin/access-codes`);
  if (!res.ok) throw new Error('Failed to fetch access codes');
  return res.json();
}
